import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, FileSignature, FileText, Eye, Edit, Copy, Trash2, Save, Loader2 } from 'lucide-react';

const OfferLetterTab = ({
  offerTemplates, templateDialogOpen, setTemplateDialogOpen,
  editingTemplate, templateForm, setTemplateForm,
  savingTemplateMulti, saveTemplate, deleteTemplate,
  openNewTemplateDialog, openEditTemplateDialog,
  duplicateTemplate, previewTemplate, previewDialogOpen,
  setPreviewDialogOpen, previewContent, formatDate
}) => (
  <>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-[#E8722A]" />Offer Letter Templates</CardTitle>
          <CardDescription>Create and manage multiple offer letter templates for different positions</CardDescription>
        </div>
        <Button onClick={openNewTemplateDialog} className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="add-template-btn"><Plus className="mr-2 h-4 w-4" />Add New Template</Button>
      </CardHeader>
      <CardContent>
        {offerTemplates.length === 0 ? (
          <div className="text-center py-12 text-[#666666]">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No templates yet</p>
            <p className="text-sm mt-1">Create your first offer letter template to get started</p>
            <Button onClick={openNewTemplateDialog} className="mt-4 bg-[#4B2D6E] hover:bg-[#3a2357]"><Plus className="mr-2 h-4 w-4" />Create First Template</Button>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Template Name</TableHead><TableHead>Position Type</TableHead><TableHead>Last Modified</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {offerTemplates.map((template) => (
                <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                  <TableCell className="font-medium">{template.name}</TableCell>
                  <TableCell><Badge variant="outline" className="bg-[#F5F5F5]">{template.position_type}</Badge></TableCell>
                  <TableCell className="text-[#666666]">{formatDate(template.updated_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => previewTemplate(template)} className="text-[#4B2D6E] hover:text-[#3a2357]" data-testid={`preview-template-${template.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => duplicateTemplate(template)} className="text-[#4B2D6E] hover:text-[#3a2357]" title="Duplicate template" data-testid={`duplicate-template-${template.id}`}><Copy className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditTemplateDialog(template)} className="text-[#E8722A] hover:text-[#D6621F]" data-testid={`edit-template-${template.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteTemplate(template.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-template-${template.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="mt-6 p-4 bg-[#F5F5F5] rounded-lg">
          <p className="font-semibold text-[#4B2D6E] mb-2">Available Merge Fields:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {['{{candidate_name}}', '{{candidate_address}}', '{{position_title}}', '{{employment_type}}', '{{start_date}}', '{{pay_rate}}', '{{service_area}}', '{{company_name}}', '{{company_address}}', '{{company_phone}}', '{{company_email}}', '{{manager_name}}', '{{manager_title}}', '{{offer_date}}', '{{offer_expiration_date}}'].map(f => (
              <code key={f} className="bg-white px-2 py-1 rounded border">{f}</code>
            ))}
          </div>
          <p className="text-xs text-[#666666] mt-3">These merge fields are automatically replaced with actual values when sending an offer letter.</p>
        </div>
      </CardContent>
    </Card>

    {/* Template Editor Dialog */}
    <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#4B2D6E]"><FileSignature className="h-5 w-5 text-[#E8722A]" />{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
          <DialogDescription>{editingTemplate ? 'Update this offer letter template' : 'Create a new offer letter template for a specific position type'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="template-name">Template Name *</Label><Input id="template-name" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g., Companion Offer Letter" data-testid="template-name-input" /></div>
            <div className="space-y-2"><Label htmlFor="template-position">Position Type *</Label><Input id="template-position" value={templateForm.position_type} onChange={(e) => setTemplateForm({ ...templateForm, position_type: e.target.value })} placeholder="e.g., Companion Caregiver, CHHA" data-testid="template-position-input" /></div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="template-content">Template Content *</Label>
            <Textarea id="template-content" value={templateForm.content} onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })} placeholder="Enter the offer letter content with merge fields..." className="min-h-[400px] font-mono text-sm" data-testid="template-content-textarea" />
          </div>
          <div className="p-3 bg-[#F5F5F5] rounded-lg text-sm">
            <p className="font-medium text-[#4B2D6E] mb-2">Quick Reference - Merge Fields:</p>
            <div className="flex flex-wrap gap-1">
              {['{{candidate_name}}', '{{position_title}}', '{{start_date}}', '{{pay_rate}}', '{{company_name}}', '{{manager_name}}', '{{offer_date}}', '{{offer_expiration_date}}'].map(field => (
                <code key={field} className="bg-white px-1.5 py-0.5 rounded border text-xs cursor-pointer hover:bg-gray-100" onClick={() => setTemplateForm({ ...templateForm, content: templateForm.content + field })}>{field}</code>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={savingTemplateMulti} className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="save-template-btn">
              {savingTemplateMulti ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : (<><Save className="mr-2 h-4 w-4" />{editingTemplate ? 'Update Template' : 'Create Template'}</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Template Preview Dialog */}
    <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#4B2D6E]"><Eye className="h-5 w-5 text-[#E8722A]" />Template Preview</DialogTitle>
          <DialogDescription>This shows the raw template with merge field placeholders</DialogDescription>
        </DialogHeader>
        <div className="mt-4 p-4 bg-white border rounded-lg font-mono text-sm whitespace-pre-wrap">{previewContent}</div>
        <div className="flex justify-end mt-4"><Button onClick={() => setPreviewDialogOpen(false)}>Close</Button></div>
      </DialogContent>
    </Dialog>
  </>
);

export { OfferLetterTab };
